// Audit log service — API_INTEGRATION.md §3.19
// Immutable audit trail for all CUD operations. Traceability: FR-AUD-001 to FR-AUD-008

import { apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'READ';

export interface AuditEntry {
  id: string;
  tenant_id: string | null;
  user_id: string;
  user_name: string | null;
  user_role: string | null;
  action: AuditAction;
  entity_type: string;
  entity_id: string | null;
  changes: Record<string, unknown> | null;
  /** State before the change, when the API handler recorded one (e.g. a bill's totals before a discount). */
  old_value?: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  ip_address: string | null;
  path: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface AuditListParams {
  page?: number;
  limit?: number;
  tenant_id?: string;
  entity_type?: string;
  action?: string;
  user_id?: string;
  entity_id?: string;
  start_date?: string;
  end_date?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// Hard cap on export size to protect the browser and the API.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 10_000;

// ─── Service ────────────────────────────────────────────────────────────────

export const auditService = {
  async list(params: AuditListParams = {}): Promise<{
    data: AuditEntry[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 50,
      tenant_id: params.tenant_id,
      entity_type: params.entity_type,
      action: params.action,
      user_id: params.user_id,
      entity_id: params.entity_id,
      start_date: params.start_date,
      end_date: params.end_date,
    });
    return apiClientPaginated<AuditEntry[]>(`/audit${q}`);
  },

  /**
   * Page through every matching audit entry up to EXPORT_MAX_ROWS so the
   * caller can build a CSV export. Throws if the result set exceeds the cap
   * — callers should narrow the filter first.
   */
  async exportAll(params: AuditListParams = {}): Promise<AuditEntry[]> {
    const first = await this.list({ ...params, page: 1, limit: EXPORT_PAGE_SIZE });
    if (first.meta.total > EXPORT_MAX_ROWS) {
      throw new Error(
        `Result set is ${first.meta.total.toLocaleString('en-IN')} entries; please narrow filters (max ${EXPORT_MAX_ROWS.toLocaleString('en-IN')}).`,
      );
    }

    const rows: AuditEntry[] = [...first.data];
    const totalPages = first.meta.totalPages;
    for (let page = 2; page <= totalPages; page += 1) {
      const next = await this.list({ ...params, page, limit: EXPORT_PAGE_SIZE });
      rows.push(...next.data);
    }
    return rows;
  },
};
